import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { invoicingGroupReducer } from "../../../state/reducers";
import { IInvoicingGroup } from "../../../utils/types/models";

const InvoicingGroupEditor: React.FC<IAddEntityScreenProps<IInvoicingGroup> | IEditEntityScreenProps<IInvoicingGroup>> = (
	props: IAddEntityScreenProps<IInvoicingGroup> | IEditEntityScreenProps<IInvoicingGroup>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(invoicingGroupReducer, getInitialState<IInvoicingGroup>());
	const initialInvoicingGroup: IInvoicingGroup = {
		id: (props as IEditEntityScreenProps<IInvoicingGroup>).recordId || 0,
		name: "",
		active: true
	};
	const [invoicingGroup, setInvoicingGroup] = useState<IInvoicingGroup>(initialInvoicingGroup);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setInvoicingGroup(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setInvoicingGroup(handleChange(invoicingGroup, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(invoicingGroup.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IInvoicingGroup): void {
		if (record && !close) {
			setInvoicingGroup(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IInvoicingGroup>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={invoicingGroup}
			endpoint={Endpoint.InvoicingGroups}
			entityState={state}
			entityType={t("invoicingGroup")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={invoicingGroup.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={invoicingGroup.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={invoicingGroup.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default InvoicingGroupEditor;
